import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';

// --- PEMETAAN NAMA NOTIFIKASI ---
export const defaultFlags = {
  playSound: false,
  powerAccuOff: false,
  overSpeed: false,
  panicButton: false,
  enterPool: false,
  exitPool: false,
  enterCustomerPoint: false,
  exitCustomerPoint: false,
  enterGeofence: false,
  exitGeofence: false,
  openDoorBack: false,
  openDoorLeft: false,
  openDoorRight: false,
  harshBraking: false,
  harshAcceleration: false,
  harshCornering: false
};

const nameToKeyMap = {
  'PLAY SOUND': 'playSound',
  'POWER ACCU OFF': 'powerAccuOff',
  'OVER SPEED': 'overSpeed',
  'PANIC BUTTON': 'panicButton',
  'ENTER POOL': 'enterPool',
  'EXIT POOL': 'exitPool',
  'ENTER CUSTOMER POINT': 'enterCustomerPoint',
  'EXIT CUSTOMER POINT': 'exitCustomerPoint',
  'ENTER GEOFENCE': 'enterGeofence',
  'EXIT GEOFENCE': 'exitGeofence',
  'OPEN DOOR BACK': 'openDoorBack',
  'OPEN DOOR LEFT': 'openDoorLeft',
  'OPEN DOOR RIGHT': 'openDoorRight',
  'HARSH BRAKING': 'harshBraking',
  'HARSH ACCELERATION': 'harshAcceleration',
  'HARSH CORNERING': 'harshCornering'
};

const keyToNameMap = Object.fromEntries(Object.entries(nameToKeyMap).map(([name, key]) => [key, name]));

// --- HELPER UNTUK SWR FETCHER ---
// Di sini kita menembak API `/notification-company/search` dengan method POST
const notificationFetcher = async ([url, payload]) => {
  try {
    const response = await fleetApi.post(url, payload);
    const resultData = response?.data?.data || response?.data || [];

    // Mulai dengan semua default false
    const newFlags = { ...defaultFlags };

    // Format ulang array dari backend menjadi object Key-Value
    if (Array.isArray(resultData)) {
      resultData.forEach((apiSetting) => {
        const apiName = apiSetting.notification_name;
        const isActive = apiSetting.is_active;
        const flagKey = nameToKeyMap[apiName];

        if (flagKey) {
          newFlags[flagKey] = isActive;
        }
      });
    }

    return newFlags;
  } catch (error) {
    console.error('Gagal menarik data Notification Setting:', error);
    throw error;
  }
};

/**
 * Custom Hook untuk MENGAMBIL Data Notifikasi.
 * Ini otomatis cache dan tidak akan tembak berkali-kali tanpa alasan.
 */
export const useNotificationSettings = () => {
  // Ambil ID secara aman
  const rawUser = localStorage.getItem('user');
  let userId = '';
  let companyId = '';

  if (rawUser) {
    try {
      const parsed = JSON.parse(rawUser);
      userId = String(parsed.id || parsed.user_id || '');
      companyId = String(parsed.company || parsed.company_id || '');
    } catch {}
  }

  // Jika auth belum siap, jangan lakukan pencarian
  const shouldFetch = Boolean(userId && companyId);

  const payload = new FilterBuilder().setPaging(0, -1).equal('user_id', userId).equal('company_id', companyId).build();

  const swrKey = shouldFetch ? ['/notification-company/search', payload] : null;

  const { data, error, isLoading, isValidating } = useSWR(swrKey, notificationFetcher, {
    revalidateOnFocus: false, // jangan otomatis refresh saat pindah tab
    keepPreviousData: true
  });

  return {
    settings: data || defaultFlags,
    isLoading: isLoading || isValidating,
    isError: error,
    swrKey // Diekspor untuk fungsi force mutate/refresh jika perlu
  };
};

/**
 * Fungsi untuk MENGUBAH / MENGUPDATE pengaturan notifikasi.
 * Dapat dipanggil langsung ketika switch/checkbox di UI diklik.
 */
export const updateNotificationSetting = async (keyName, isActiveValue) => {
  try {
    const rawUser = localStorage.getItem('user');
    let userId = '';
    let companyId = '';

    if (rawUser) {
      const parsed = JSON.parse(rawUser);
      userId = String(parsed.id || parsed.user_id || '');
      companyId = String(parsed.company || parsed.company_id || '');
    }

    const notification_name = keyToNameMap[keyName];

    if (!notification_name || !userId || !companyId) {
      throw new Error(`Invalid data: key=${keyName}, userId=${userId}`);
    }

    const payload = {
      company_id: Number(companyId),
      user_id: Number(userId),
      notification_name: notification_name,
      is_active: isActiveValue
    };

    // Tembak API Upsert (Ubah data di backend)
    await fleetApi.post('/notification-company/upsert', payload);

    // Setelah berhasil tembak ke backend, Refresh paksa semua hook yang pakai SWR
    // Ini menggantikan peran reducer di Redux
    mutate((key) => Array.isArray(key) && key[0] === '/notification-company/search', undefined, { revalidate: true });

    return true;
  } catch (error) {
    console.error('Gagal memperbarui notifikasi:', error);
    throw error;
  }
};
